import { isObject } from "es-toolkit/compat";
import {
  getColumnByNameAndTableName,
  getColumnOptionsLookupPropAndColumnName,
  getColumnValueFromRowData
} from "lib/utils";
import { ApiRecordType } from "types/apiTypes";
import { TableColumnType } from "types/baTypes";
import { RecordItem } from "types/common";
import { NOTES_LINKS_TABLE, USER_TABLE_NAME } from "./constants";
import { ExtendedSchema } from "./schema";

export const getColumnsForNotes = (finalColumnOptions: TableColumnType[]) => {
  const nameColumn = getColumnByNameAndTableName(finalColumnOptions, "name", "notes");
  const messageColumn = getColumnByNameAndTableName(finalColumnOptions, "body_html", "notes");
  const fileColumn = getColumnByNameAndTableName(finalColumnOptions, "", "notes_files");
  const createdByColumn = getColumnByNameAndTableName(finalColumnOptions, "full_name", USER_TABLE_NAME);
  const createdAtColumn = getColumnByNameAndTableName(finalColumnOptions, "created_at", "notes");
  const xyCoordinateColumn = getColumnByNameAndTableName(finalColumnOptions, "", "files_annotations");

  const finalColumns: RecordItem = {};

  if (nameColumn) {
    finalColumns["name"] = nameColumn;
  }

  if (messageColumn) {
    finalColumns["body_html"] = messageColumn;
  }

  if (fileColumn) {
    finalColumns["files"] = fileColumn;
  }

  if (createdByColumn) {
    finalColumns["createdBy"] = createdByColumn;
  }

  if (createdAtColumn) {
    finalColumns["createdAt"] = createdAtColumn;
  }

  if (xyCoordinateColumn) {
    finalColumns["xyCoordinate"] = xyCoordinateColumn;
  }

  return finalColumns;
};

export const getNotesLinkForeignKey = (
  parentTableName: string,
  extendedSchema: ExtendedSchema,
  skipUserTableCheck?: boolean
) => {
  const noteLinksTableProps = extendedSchema?.[NOTES_LINKS_TABLE];
  const noteLinkFK = noteLinksTableProps?.fk?.find(
    (fkItem) =>
      fkItem.table === parentTableName &&
      (!skipUserTableCheck ? parentTableName !== USER_TABLE_NAME : true) &&
      !["created_by", "updated_by"].includes(fkItem.attributeId)
  );

  return noteLinkFK;
};

export const transformDataForNoteView = ({
  data,
  tableName,
  finalColumnOptions,
  recordTypesData
}: {
  data?: RecordItem[];
  tableName: string;
  finalColumnOptions: TableColumnType[];
  recordTypesData?: ApiRecordType[];
}) => {
  if (!data?.length) return [];
  const columnsForNotes = getColumnsForNotes(finalColumnOptions);
  const createdByColumn = columnsForNotes.createdBy;
  if (tableName === "notes") {
    return (
      data?.map((item: RecordItem) => ({
        ...item,
        created_by: item?.[createdByColumn?.lookupPath?.["0"]?.lookupColumnLabel],
        sourceTitle: item.source_title,
        sourceUrl: item.source_url,
        sourceRootTitle: item.source_root_title,
        sourceRootUrl: item.source_root_url
      })) || []
    );
  }

  return data
    .map((row: RecordItem) => {
      const finalValue: RecordItem = {
        ...row,
        name: "",
        message: "",
        notes_files: row.notes_files ?? []
      };

      const nameColumn = columnsForNotes.name;
      const messageColumn = columnsForNotes.body_html;
      const fileColumn = columnsForNotes.files;
      const createdAtColumn = columnsForNotes.createdAt;

      if (isObject(finalValue.note_id)) {
        finalValue.note_id = (finalValue.note_id as RecordItem)?.id;
      }

      if (nameColumn) {
        const name = getColumnValueFromRowData({
          row,
          col: nameColumn,
          colOptions: [nameColumn],
          recordTypesData
        });

        if (Array.isArray(name)) {
          finalValue["name"] = name[0]?.name;
          finalValue["sourceTitle"] = name[0]?.source_title;
          finalValue["sourceUrl"] = name[0]?.source_url;
          finalValue["sourceRootTitle"] = name[0]?.source_root_title;
          finalValue["sourceRootUrl"] = name[0]?.source_root_url;
        } else if (typeof name === "string") {
          finalValue["name"] = name;
        }
      }

      if (messageColumn) {
        const message = getColumnValueFromRowData({
          row,
          col: messageColumn,
          colOptions: [messageColumn],
          recordTypesData
        });

        if (Array.isArray(message)) {
          finalValue["message"] = message[0]?.message;
        } else if (typeof message === "string") {
          finalValue["message"] = message;
        }
      }

      if (fileColumn) {
        const files = getColumnValueFromRowData({
          row,
          col: fileColumn,
          colOptions: [fileColumn],
          recordTypesData
        });
        finalValue["files"] = files;
      }
      // TODO: FIX THIS IMPLEMENTATION

      if (createdByColumn) {
        const createdBy = getColumnValueFromRowData({
          row,
          col: createdByColumn,
          colOptions: [createdByColumn],
          recordTypesData
        });
        finalValue["created_by"] = createdBy?.[0];
      }

      if (createdAtColumn) {
        const createdAt = getColumnValueFromRowData({
          row,
          col: createdAtColumn,
          colOptions: [createdAtColumn],
          recordTypesData
        });
        const finalProperty: null | { lookupCols: string } = getColumnOptionsLookupPropAndColumnName(createdAtColumn);

        if (finalProperty?.lookupCols && Array.isArray(createdAt) && createdAtColumn.isLookup) {
          finalValue["created_at"] = createdAt[0]?.[finalProperty.lookupCols];
        }
      }

      if (finalValue["name"] || finalValue["message"] || finalValue["notes_files"]?.length) {
        return finalValue;
      }

      return null;
    })
    ?.filter(Boolean) as RecordItem[];
};
